import { HTTP_METHOD } from '@squareup/dex-types-shared-http';
import { ApiExplorerFieldDropdownOption } from '@squareup/dex-ui-shared-api-explorer-providers';

interface EndpointLookupDetails {
  path: string;
  resultJsonPointer: string;
  method: HTTP_METHOD;
  mapperFn: (data: unknown) => Array<ApiExplorerFieldDropdownOption>;

  dependsOn?: Array<EndpointLookupDetails>;
  requestMapperFn?: (
    dependsOnResults?: Array<{
      dependsOnEndpoint: EndpointLookupDetails;
      result: unknown;
    }>
  ) => Record<string, unknown>;
}

const locationLookup: EndpointLookupDetails = {
  path: '/v2/locations',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/locations',
  mapperFn: (data: unknown) => {
    // V2 Location
    const locations = data as Array<{ id: string; name: string }>;
    return locations.map((location) => ({
      value: location.id,
      description: location.name,
    }));
  },
};

type CatalogObjectData = {
  id: string;
  type?: string;
  [key: string]: unknown;
};

function flattenCatalogVariations(
  data: Array<CatalogObjectData>
): Array<CatalogObjectData> {
  return data.flatMap((obj) => {
    // Flatten variations
    if (
      obj.type === 'ITEM' &&
      (obj['item_data'] as { variations: Array<CatalogObjectData> }).variations
    ) {
      return [
        obj,
        ...(obj['item_data'] as { variations: Array<CatalogObjectData> })
          .variations,
      ];
    } else {
      return obj;
    }
  });
}

const catalogLookup: EndpointLookupDetails = {
  path: '/v2/catalog/list',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/objects',
  mapperFn: (data: unknown, filter?: string) => {
    // V2 Catalog Object
    const catalogObjects = flattenCatalogVariations(
      data as Array<CatalogObjectData>
    );

    return catalogObjects
      .filter((obj) => (filter ? obj.type === filter : true))
      .map((obj) => {
        // Attempt to get the name of the catalog object
        const dataField = obj[`${obj.type?.toLowerCase() ?? ''}_data`] as
          | { name: string }
          | undefined;
        return {
          value: obj.id,
          description: `${obj.type} - ${dataField?.name ?? ''}`,
        };
      });
  },
};

const catalogVariationLookup: EndpointLookupDetails = {
  ...catalogLookup,
  mapperFn(data) {
    return (
      catalogLookup as unknown as {
        mapperFn: (
          data: unknown,
          filter?: string
        ) => ApiExplorerFieldDropdownOption[];
      }
    ).mapperFn(data, 'ITEM_VARIATION');
  },
};

const customerLookup: EndpointLookupDetails = {
  path: '/v2/customers',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/customers',
  mapperFn: (data: unknown) => {
    // V2 Customer
    const customers = data as Array<{
      id: string;
      given_name?: string;
      family_name?: string;
    }>;
    return customers.map((customer) => {
      let description = customer.given_name ? `${customer.given_name} ` : '';
      if (customer.family_name) {
        description += customer.family_name;
      }
      return {
        value: customer.id,
        description,
      };
    });
  },
};

const cardsLookup: EndpointLookupDetails = {
  path: '/v2/cards',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/cards',
  mapperFn: (data: unknown) => {
    // V2 Cards
    const cards = data as Array<{
      id: string;
      cardholder_name: string;
      last_4: string;
    }>;
    return cards.map((card) => ({
      value: card.id,
      description: `${card.cardholder_name} - ${card.last_4}`,
    }));
  },
};

const teamMembersLookup: EndpointLookupDetails = {
  path: '/v2/team-members/search',
  method: HTTP_METHOD.POST,
  resultJsonPointer: '/team_members',
  mapperFn: (data: unknown) => {
    // V2 Team Member
    const teamMembers = data as Array<{
      id: string;
      given_name: string;
      family_name: string;
    }>;
    return teamMembers.map((teamMember) => {
      let description = teamMember.given_name
        ? `${teamMember.given_name} `
        : '';
      if (teamMember.family_name) {
        description += teamMember.family_name;
      }
      return {
        value: teamMember.id,
        description,
      };
    });
  },
};

const subscriptionPlansLookup: EndpointLookupDetails = {
  ...catalogLookup,
  mapperFn(data) {
    return (
      catalogLookup as unknown as {
        mapperFn: (
          data: unknown,
          filter?: string
        ) => ApiExplorerFieldDropdownOption[];
      }
    ).mapperFn(data, 'SUBSCRIPTION_PLAN');
  },
};

const subscriptionPlanVariationsLookup: EndpointLookupDetails = {
  ...catalogLookup,
  mapperFn(data) {
    return (
      catalogLookup as unknown as {
        mapperFn: (
          data: unknown,
          filter?: string
        ) => ApiExplorerFieldDropdownOption[];
      }
    ).mapperFn(data, 'SUBSCRIPTION_PLAN_VARIATION');
  },
};

const subscriptionsLookup: EndpointLookupDetails = {
  path: '/v2/subscriptions/search',
  method: HTTP_METHOD.POST,
  resultJsonPointer: '/subscriptions',
  mapperFn: (data: unknown) => {
    // V2 Subscriptions
    const subscriptions = data as Array<{
      id: string;
      created_at: string;
    }>;
    return subscriptions.map((subscription) => ({
      value: subscription.id,
      description: `${subscription.created_at}`,
    }));
  },
};

const disputesLookup: EndpointLookupDetails = {
  path: '/v2/disputes',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/disputes',
  mapperFn: (data: unknown) => {
    // V2 Dispute
    const disputes = data as Array<{
      id: string;
      reason: string;
      state: string;
    }>;

    return disputes.map((dispute) => ({
      value: dispute.id,
      description: `${dispute.reason} - ${dispute.state}`,
    }));
  },
};

const ordersLookup: EndpointLookupDetails = {
  path: '/v2/orders/search',
  method: HTTP_METHOD.POST,
  resultJsonPointer: '/orders',
  mapperFn: (data: unknown) => {
    // V2 Orders
    const orders = data as Array<{
      id: string;
      created_at: string;
      source?: {
        name: string;
      };
    }>;

    return orders.map((order) => ({
      value: order.id,
      description: `${order.source?.name || ''} - ${order.created_at}`,
    }));
  },
  dependsOn: [locationLookup],
  requestMapperFn(dependsOnResults) {
    const locationIds = dependsOnResults?.find(
      (value) => value.dependsOnEndpoint === locationLookup
    )?.result as Array<{ id: string }> | undefined;

    // Ensure it's an array
    if (!locationIds?.length) {
      return {};
    }

    return {
      location_ids: locationIds.map((location) => location.id),
    };
  },
};

const bookingsLookup: EndpointLookupDetails = {
  path: '/v2/bookings',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/bookings',
  mapperFn: (data: unknown) => {
    // V2 booking
    const bookings = data as Array<{ id: string; seller_note: string }>;
    return bookings.map((booking) => ({
      value: booking.id,
      description: booking.seller_note,
    }));
  },
};

const loyaltyProgramLookup: EndpointLookupDetails = {
  path: '/v2/loyalty/programs/main',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/program',
  mapperFn: (data: unknown) => {
    const program = data as { id: string; status: string };
    return [
      {
        value: program.id,
        description: program.status,
      },
    ];
  },
};

const loyaltyRewardTiersLookup: EndpointLookupDetails = {
  path: '/v2/loyalty/programs/main',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/program',
  mapperFn: (data: unknown) => {
    const program = data as {
      id: string;
      reward_tiers: Array<{ id: string; name: string; points: number }>;
    };

    return program.reward_tiers.map((tier) => {
      return {
        value: tier.id,
        description: `${tier.name} - ${tier.points}`,
      };
    });
  },
};

const loyaltyAccountsLookup: EndpointLookupDetails = {
  path: '/v2/loyalty/accounts/search',
  method: HTTP_METHOD.POST,
  resultJsonPointer: '/loyalty_accounts',
  mapperFn: (data: unknown) => {
    const accounts = data as Array<{ id: string; balance: number }>;
    return accounts.map((account) => {
      return {
        value: account.id,
        description: `Balance - ${account.balance}`,
      };
    });
  },
};

const loyaltyRewardLookup: EndpointLookupDetails = {
  path: '/v2/loyalty/rewards/search',
  method: HTTP_METHOD.POST,
  resultJsonPointer: '/rewards',
  mapperFn: (data: unknown) => {
    const rewards = data as Array<{ id: string; status: number }>;
    return rewards.map((rewards) => {
      return {
        value: rewards.id,
        description: `Status - ${rewards.status}`,
      };
    });
  },
};

const employeeLookup: EndpointLookupDetails = {
  path: '/v2/employees',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/employees',
  mapperFn: (data: unknown) => {
    const employees = data as Array<{
      id: string;
      first_name: string;
      last_name: string;
    }>;
    return employees.map((employee) => ({
      value: employee.id,
      description: `${employee.first_name} ${employee.last_name}`,
    }));
  },
};

const paymentLookup: EndpointLookupDetails = {
  path: '/v2/payments',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/payments',
  mapperFn: (data: unknown) => {
    const payments = data as Array<{
      id: string;
      amount_money?: {
        amount: number;
        currency: string;
      };
      source_type: string;
    }>;
    return payments.map((payment) => ({
      value: payment.id,
      description: `${payment.source_type} - ${payment.amount_money?.amount} ${payment.amount_money?.currency}`,
    }));
  },
};

const giftCardLookup: EndpointLookupDetails = {
  path: '/v2/gift-cards',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/gift_cards',
  mapperFn: (data: unknown) => {
    const giftCards = data as Array<{
      id: string;
      type: string;
      balance_money?: {
        amount: number;
        currency: string;
      };
    }>;
    return giftCards.map((card) => ({
      value: card.id,
      description: `${card.type} - Balance: ${card.balance_money?.amount} ${card.balance_money?.currency}`,
    }));
  },
};

const merchantLookup: EndpointLookupDetails = {
  path: '/v2/merchants',
  method: HTTP_METHOD.GET,
  resultJsonPointer: '/merchant',
  mapperFn: (data: unknown) => {
    const merchants = data as Array<{
      id: string;
      business_name: string;
    }>;
    return merchants.map((merchant) => ({
      value: merchant.id,
      description: merchant.business_name,
    }));
  },
};

const endpointLookup: Record<string, EndpointLookupDetails> = {
  // Locations
  location_id: locationLookup,
  location_ids: locationLookup,

  // Bookings
  booking_id: bookingsLookup,
  booking_ids: bookingsLookup,

  // Customers
  customer_id: customerLookup,
  customer_ids: customerLookup,

  // Disputes
  dispute_id: disputesLookup,

  // Catalog
  catalog_object_id: catalogLookup,
  catalog_object_ids: catalogLookup,
  item_id: catalogLookup,
  item_ids: catalogLookup,
  object_id: catalogLookup,
  object_ids: catalogLookup,
  service_variation_id: catalogVariationLookup,
  service_variation_ids: catalogVariationLookup,

  // Cards
  card_id: cardsLookup,
  cards_id: cardsLookup,

  // Gift cards
  gift_card_id: giftCardLookup,
  gift_card_ids: giftCardLookup,

  // Employees
  employee_id: employeeLookup,
  employee_ids: employeeLookup,

  // Loyalty
  program_id: loyaltyProgramLookup,
  account_id: loyaltyAccountsLookup,
  account_ids: loyaltyAccountsLookup,
  loyalty_account_id: loyaltyAccountsLookup,
  loyalty_account_ids: loyaltyAccountsLookup,
  reward_id: loyaltyRewardLookup,
  reward_ids: loyaltyRewardLookup,
  reward_tier_id: loyaltyRewardTiersLookup,
  reward_tier_ids: loyaltyRewardTiersLookup,

  // Merchants
  merchant_id: merchantLookup,
  merchant_ids: merchantLookup,

  // Orders
  order_id: ordersLookup,
  order_ids: ordersLookup,

  // Payments
  payment_id: paymentLookup,
  payment_ids: paymentLookup,

  // Subscriptions
  subscription_plan_id: subscriptionPlansLookup,
  subscription_plan_ids: subscriptionPlansLookup,

  plan_variation_id: subscriptionPlanVariationsLookup,
  plan_variation_ids: subscriptionPlanVariationsLookup,
  subscription_plan_variation_id: subscriptionPlanVariationsLookup,
  subscription_plan_variation_ids: subscriptionPlanVariationsLookup,
  successor_plan_variation_id: subscriptionPlanVariationsLookup,

  subscription_id: subscriptionsLookup,
  subscription_ids: subscriptionsLookup,

  // Team members
  team_member_id: teamMembersLookup,
  team_member_ids: teamMembersLookup,
};

export { endpointLookup, type EndpointLookupDetails };
